import { ctaClasses, BACK_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';

import type { OnePager as OnePagerData } from './one-pager-content';
import type { ReactElement } from 'react';

/**
 * AF-138 — presentational ICP one-pager. Renders a single archetype's
 * one-pager: lede + sections of bulleted points. Controlled/pure — the
 * structure comes from `one-pager-content.ts` and every display string is
 * resolved from the `onepagers.*` catalog via `useT()`.
 *
 * ## Direction follows the LOCALE, never the archetype
 *
 * This used to stamp `dir="rtl"` on the Hebrew-B2C sheet, on every locale. The
 * archetype is ABOUT a Hebrew-speaking market; the sheet is written in whatever
 * language the visitor is reading. So an English reader of
 * `/one-pagers/hebrew-b2c` got English prose laid out right-to-left: every
 * sentence ended with a leading full stop (".payment inside the chat"), the
 * bullets sat on the right with their markers, and a sales asset read as
 * broken — in four of the six locales, on a page whose whole job is to be
 * forwarded to a buyer.
 *
 * The two locales that genuinely want RTL already get it: `<html dir>` is set
 * per locale by the shell, so `/he/one-pagers/hebrew-b2c` mirrors correctly and
 * inherits it here. An element-level override could only ever disagree with
 * that, and it did.
 */
export function OnePager({ data }: { readonly data: OnePagerData }): ReactElement {
  const { t } = useT();
  return (
    <main
      id="main"
      tabIndex={-1}
      data-syncanix-one-pager={data.slug}
      className="bg-[color:var(--bd-page)]"
    >
      {/* Two lines above the title: the "back to all" breadcrumb and the ICP
          archetype this sheet is written for. The archetype is the more useful
          of the two to a reader who arrived from a link, so it takes the eyebrow
          slot and the breadcrumb sits above it in the same mono voice. */}
      <PageHero
        align="start"
        width="prose"
        eyebrow={
          <span className="flex flex-col gap-1.5">
            <Link className={`self-start ${BACK_LINK}`} to="/one-pagers">
              {t('onepagers.detail.backToAll')}
            </Link>
            <span className="text-[color:var(--bd-ink)]">{t(data.archetypeKey)}</span>
          </span>
        }
        title={t(data.titleKey)}
      />

      <div className="mx-auto max-w-3xl break-words px-6 pb-20 pt-10 text-[1.02rem] leading-[1.7] text-[color:var(--bd-body)]">
        {/* The lede is a pull-quote: it states the sheet's argument in one
            sentence, so it carries the brand rule rather than the neutral
            hairline it used to. */}
        {/* `border-color` is set as well as `border-image`: where the image is
            unsupported the border falls back to its colour, and unset that is
            `currentColor` — a 3px rule in body ink, which reads as a mistake
            rather than as a quieter version of the same device. */}
        <p
          className="border-s-[3px] border-[color:var(--bd-line-3)] ps-5 text-[1.12rem] leading-[1.6] text-[color:var(--bd-ink)]"
          style={{ borderImage: 'var(--bd-gradient) 1' }}
        >
          {t(data.ledeKey)}
        </p>

        {data.sections.map((section) => (
          <section key={section.headingKey} className="mt-11">
            <h2 className="text-[1.35rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]">
              {t(section.headingKey)}
            </h2>
            <ul className="mt-4 flex list-disc flex-col gap-2.5 ps-6 marker:text-[color:var(--bd-dot)]">
              {section.bulletKeys.map((bulletKey) => (
                <li key={bulletKey} className="ps-1">
                  {t(bulletKey)}
                </li>
              ))}
            </ul>
          </section>
        ))}

        <footer className="mt-14 flex flex-wrap items-center gap-3 border-t border-[color:var(--bd-line)] pt-8">
          <Link to="/pricing" className={ctaClasses({ size: 'md' })}>
            {t('onepagers.detail.seePricing')}
          </Link>
          {/* Static PDF rendered at build time by scripts/generate-one-pager-pdfs.ts.
              Plain <a> (not <Link>) — it's an asset, not a SPA route. */}
          <a
            href={`/one-pagers/${data.slug}.pdf`}
            download
            className={ctaClasses({ variant: 'secondary', size: 'md' })}
          >
            {t('onepagers.detail.downloadPdf')}
          </a>
        </footer>
      </div>
    </main>
  );
}
